import { Component, OnDestroy, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  StaffTransportService,
  BusListItem,
  BusStop,
  TransportStudent,
  LatestVehicleLocationInfo,
} from '../services/staff-transport.service';
import { StaffSessionService } from '../services/staff-session.service';

declare var customToastr: any;
declare var google: any;

// Same key already used by the admin Transport module (transport.component.ts)
// — reused here rather than adding a second Maps script load.
const GOOGLE_MAPS_API_KEY = 'AIzaSyDHWle0ySVx-1H7fKACXpMERZ01g9gyEIM';
const VEHICLE_LOCATION_POLL_MS = 6000;

type TransportView = 'busList' | 'studentRoster' | 'liveMap';

@Component({
  selector: 'app-staff-transport',
  templateUrl: './staff-transport.component.html',
  styleUrls: ['../shared/staff-common.css', './staff-transport.component.css']
})
export class StaffTransportComponent implements OnInit, OnDestroy {
  view: TransportView = 'busList';

  staffId: number | string | null = null;
  isSuperAdmin = false;

  // ---------- Bus list ----------
  buses: BusListItem[] = [];
  loadingBuses = true;
  selfAssignedOnly = false;
  searchTerm = '';

  showStopsSheet = false;
  stopsForBus: BusStop[] = [];
  loadingStops = false;
  stopsBusName = '';

  readonly skeletonBuses = Array.from({ length: 6 }, () => ({}));

  // ---------- Student roster ----------
  selectedBus: BusListItem | null = null;
  students: TransportStudent[] = [];
  loadingStudents = true;
  studentSearchOpen = false;
  studentSearchTerm = '';
  vehicleTrackingEnabled = false;

  readonly skeletonStudents = Array.from({ length: 10 }, () => ({}));

  // ---------- Live map ----------
  vehicleLocation: LatestVehicleLocationInfo | null = null;
  fetchingLocation = true;
  private map: any = null;
  private marker: any = null;
  private directionsRenderer: any = null;
  private mapsScriptLoading: Promise<void> | null = null;
  private pollHandle: any = null;

  constructor(
    private staffTransportService: StaffTransportService,
    private staffSession: StaffSessionService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    const staffDetails = this.staffSession.getStaffDetails();
    const info = this.staffSession.getSchoolAndStaffInfo();
    this.staffId = staffDetails?.staffId ?? info?.StaffId ?? null;
    this.isSuperAdmin = !!(info?.isSuperAdmin ?? staffDetails?.isSuperAdmin);
    this.loadBuses();
  }

  ngOnDestroy(): void {
    this.stopVehiclePolling();
  }

  // ---------- Bus list ----------

  async loadBuses() {
    if (!this.staffId) { this.loadingBuses = false; return; }
    this.loadingBuses = true;
    try {
      const response = await firstValueFrom(this.staffTransportService.getBusList(this.staffId, this.selfAssignedOnly));
      this.buses = response?.status ? (response.body?.busList || []) : [];
    } catch {
      this.buses = [];
      const message = await firstValueFrom(this.translate.get('somethingWentWrong'));
      customToastr(message, 'error');
    } finally {
      this.loadingBuses = false;
    }
  }

  // Only Super Admins get the toggle at all — a plain staff member always
  // sees the full bus list.
  toggleSelfAssigned() {
    if (!this.isSuperAdmin) { return; }
    this.selfAssignedOnly = !this.selfAssignedOnly;
    this.loadBuses();
  }

  get filteredBuses(): BusListItem[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) { return this.buses; }
    return this.buses.filter(b =>
      (b.driverName || '').toLowerCase().includes(term) ||
      (b.driverMobileNumber || '').toLowerCase().includes(term) ||
      (b.busNumber || '').toLowerCase().includes(term) ||
      (b.busName || '').toLowerCase().includes(term)
    );
  }

  async openStops(bus: BusListItem) {
    if (!this.staffId) { return; }
    this.showStopsSheet = true;
    this.stopsBusName = bus.busNumber || bus.busName;
    this.stopsForBus = [];
    this.loadingStops = true;
    try {
      const response = await firstValueFrom(this.staffTransportService.getBusStops(this.staffId, bus.busId));
      this.stopsForBus = response?.status ? (response.body?.busStopList || []) : [];
    } catch {
      this.stopsForBus = [];
    } finally {
      this.loadingStops = false;
    }
  }

  closeStopsSheet() {
    this.showStopsSheet = false;
  }

  callNumber(number: string) {
    if (number) {
      window.open(`tel:${number}`, '_self');
    }
  }

  // ---------- Student roster ----------

  async openBus(bus: BusListItem) {
    this.selectedBus = bus;
    this.view = 'studentRoster';
    this.studentSearchOpen = false;
    this.studentSearchTerm = '';
    this.vehicleTrackingEnabled = this.staffSession.isFeatureEnabled('VEHICLE_TRACKING');
    await this.loadStudentsForBus();
  }

  private async loadStudentsForBus() {
    if (!this.staffId || !this.selectedBus) { return; }
    this.loadingStudents = true;
    try {
      const response = await firstValueFrom(this.staffTransportService.getStudentListForBus(this.staffId, this.selectedBus.busId));
      this.students = response?.status ? (response.body?.studentList || []) : [];
    } catch {
      this.students = [];
      const message = await firstValueFrom(this.translate.get('somethingWentWrong'));
      customToastr(message, 'error');
    } finally {
      this.loadingStudents = false;
    }
  }

  toggleStudentSearch() {
    this.studentSearchOpen = !this.studentSearchOpen;
    if (!this.studentSearchOpen) { this.studentSearchTerm = ''; }
  }

  get filteredStudents(): TransportStudent[] {
    const term = this.studentSearchTerm.trim().toLowerCase();
    if (!term) { return this.students; }
    return this.students.filter(s =>
      (s.studentFullName || '').toLowerCase().includes(term) ||
      (s.parentName || '').toLowerCase().includes(term) ||
      (s.classDivision || '').toLowerCase().includes(term) ||
      (s.mobileNumber || '').toLowerCase().includes(term)
    );
  }

  backToBusList() {
    this.view = 'busList';
    this.selectedBus = null;
    this.stopVehiclePolling();
  }

  // ---------- Live map ----------

  async openLiveMap() {
    if (!this.selectedBus) { return; }
    this.view = 'liveMap';
    this.vehicleLocation = null;
    this.fetchingLocation = true;
    await this.loadMapsScript();
    // #sf-transport-map only exists once *ngIf="!fetchingLocation" flips —
    // fetchVehicleLocation() flips it, so wait one macrotask for Angular to
    // paint the container before touching the DOM.
    await this.fetchVehicleLocation();
    setTimeout(() => this.initMap());
    this.startVehiclePolling();
  }

  backToStudentRoster() {
    this.stopVehiclePolling();
    this.view = 'studentRoster';
  }

  private loadMapsScript(): Promise<void> {
    if (typeof google !== 'undefined' && google.maps) { return Promise.resolve(); }
    if (this.mapsScriptLoading) { return this.mapsScriptLoading; }
    this.mapsScriptLoading = new Promise<void>((resolve) => {
      const existing = document.getElementById('sf-transport-google-maps-script');
      if (existing) {
        existing.addEventListener('load', () => resolve());
        return;
      }
      const script = document.createElement('script');
      script.id = 'sf-transport-google-maps-script';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      document.head.appendChild(script);
    });
    return this.mapsScriptLoading;
  }

  private initMap() {
    const startLat = parseFloat(String(this.selectedBus?.startPointLatitude)) || 0;
    const startLng = parseFloat(String(this.selectedBus?.startPointLongitude)) || 0;
    const el = document.getElementById('sf-transport-map');
    if (!el) { return; }

    this.map = new google.maps.Map(el, {
      center: { lat: startLat, lng: startLng },
      zoom: 14,
    });

    this.marker = new google.maps.Marker({
      map: this.map,
      title: this.selectedBus?.busNumber,
    });

    this.drawRoute();
  }

  private drawRoute() {
    if (!this.selectedBus) { return; }
    const origin = {
      lat: parseFloat(String(this.selectedBus.startPointLatitude)) || 0,
      lng: parseFloat(String(this.selectedBus.startPointLongitude)) || 0,
    };
    const destination = {
      lat: parseFloat(String(this.selectedBus.endPointLatitude)) || 0,
      lng: parseFloat(String(this.selectedBus.endPointLongitude)) || 0,
    };
    const directionsService = new google.maps.DirectionsService();
    this.directionsRenderer = new google.maps.DirectionsRenderer({ map: this.map, suppressMarkers: true });
    directionsService.route(
      { origin, destination, travelMode: google.maps.TravelMode.DRIVING },
      (result: any, status: string) => {
        if (status === 'OK') {
          this.directionsRenderer.setDirections(result);
        }
      }
    );
  }

  private async fetchVehicleLocation() {
    if (!this.staffId || !this.selectedBus) { return; }
    try {
      const response = await firstValueFrom(this.staffTransportService.getVehicleLocation(this.staffId, this.selectedBus.busId));
      if (response?.status && response.body?.latestVehicleLocationInfo) {
        this.vehicleLocation = response.body.latestVehicleLocationInfo;
        this.updateMarkerPosition();
      }
      // A falsy/failed response keeps the last known location on screen —
      // matches the mobile app's own behavior (no reset, no error toast).
    } catch {
      // Silently keep polling — same as the mobile app's swallow-and-retry.
    } finally {
      this.fetchingLocation = false;
    }
  }

  private updateMarkerPosition() {
    if (!this.vehicleLocation) { return; }
    const lat = parseFloat(String(this.vehicleLocation.latitude)) || 0;
    const lng = parseFloat(String(this.vehicleLocation.longitude)) || 0;
    if (!this.map) {
      // Defensive fallback — openLiveMap() normally initializes the map
      // right after the first fetch resolves, but if this runs first for
      // any reason, initialize now instead of dropping the update.
      this.initMap();
    }
    if (this.marker) {
      this.marker.setPosition({ lat, lng });
    }
    if (this.map) {
      this.map.panTo({ lat, lng });
    }
  }

  private startVehiclePolling() {
    this.stopVehiclePolling();
    this.pollHandle = setInterval(() => this.fetchVehicleLocation(), VEHICLE_LOCATION_POLL_MS);
  }

  private stopVehiclePolling() {
    if (this.pollHandle) {
      clearInterval(this.pollHandle);
      this.pollHandle = null;
    }
  }

  get vehicleSpeedKmh(): number {
    return parseInt(String(this.vehicleLocation?.speed), 10) || 0;
  }
}
